import React, { useState, useCallback } from 'react';
import {
    DndContext,
    DragOverlay,
    DragStartEvent,
    DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter
} from '@dnd-kit/core';
import { Move, CheckSquare } from 'lucide-react';
import { RackStructure } from '../rack-builder/types';
import { LocationHeatmapDto } from './types';
import { ActiveMoveBin } from './heatmapMoveTypes';
import { InteractiveRackBody } from './InteractiveRackBody';

interface InteractiveHeatmapRackGridProps {
    rackStructure: RackStructure;
    heatmapData: Map<string, LocationHeatmapDto>;
    onBinMoveRequested: (fromBinCode: string, toBinCode: string) => void;
    onBatchMoveRequested?: (sourceBinCodes: string[], toBinCode: string) => void;
    selectedBinCodes: Set<string>;
    onToggleSelect: (binCode: string, mode: 'single' | 'add' | 'range') => void;
    onBinContextMenu?: (binCode: string, x: number, y: number) => void;
    emptyBinCodes?: Set<string>;
    activeMoveBins?: Map<string, ActiveMoveBin[]>;
}

/**
 * Single-rack drag&drop heatmap view. Owns its own DndContext.
 * For cross-rack views, see DualRackView which uses InteractiveRackBody directly.
 */
export const InteractiveHeatmapRackGrid: React.FC<InteractiveHeatmapRackGridProps> = ({
    rackStructure,
    heatmapData,
    onBinMoveRequested,
    onBatchMoveRequested,
    selectedBinCodes,
    onToggleSelect,
    onBinContextMenu,
    emptyBinCodes,
    activeMoveBins
}) => {
    const [activeBinCode, setActiveBinCode] = useState<string | null>(null);
    const [activeIsBatch, setActiveIsBatch] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
    );

    const handleDragStart = useCallback((event: DragStartEvent) => {
        const draggedBin = String(event.active.id);
        setActiveBinCode(draggedBin);
        const isBatch = selectedBinCodes.has(draggedBin) && selectedBinCodes.size > 1;
        setActiveIsBatch(isBatch);
    }, [selectedBinCodes]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const from = activeBinCode;
        const to = event.over ? String(event.over.id) : null;
        const wasBatch = activeIsBatch;
        setActiveBinCode(null);
        setActiveIsBatch(false);

        if (!from || !to || from === to) return;

        if (wasBatch && onBatchMoveRequested) {
            const sources = Array.from(selectedBinCodes).filter(b => b !== to);
            if (sources.length > 0) onBatchMoveRequested(sources, to);
        } else {
            onBinMoveRequested(from, to);
        }
    }, [activeBinCode, activeIsBatch, onBinMoveRequested, onBatchMoveRequested, selectedBinCodes]);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <div className="w-full h-full flex flex-col">
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded-lg mb-2 flex items-center space-x-2 flex-shrink-0">
                    <Move className="w-4 h-4" />
                    <span>
                        <strong>Rediger-modus:</strong> Dra for å flytte alt i bin. Ctrl/Cmd+klikk for multi-valg. Høyreklikk for forslag / tøm-bin.
                        {emptyBinCodes && emptyBinCodes.size > 0 && (
                            <span className="ml-2 text-green-700">
                                · <span className="font-semibold">{emptyBinCodes.size} tomme</span> (grønn stiplet)
                            </span>
                        )}
                        {activeMoveBins && activeMoveBins.size > 0 && (
                            <span className="ml-2 text-orange-700">
                                · <span className="font-semibold">{activeMoveBins.size} med pågående flytt</span> (oransje ring)
                            </span>
                        )}
                    </span>
                </div>
                <div className="flex-1 rounded-lg overflow-hidden">
                    <InteractiveRackBody
                        rackStructure={rackStructure}
                        heatmapData={heatmapData}
                        activeBinCode={activeBinCode}
                        activeIsBatch={activeIsBatch}
                        selectedBinCodes={selectedBinCodes}
                        onToggleSelect={onToggleSelect}
                        onBinContextMenu={onBinContextMenu}
                        emptyBinCodes={emptyBinCodes}
                        activeMoveBins={activeMoveBins}
                    />
                </div>
            </div>

            <DragOverlay>
                {activeBinCode ? (
                    <div className="w-32 h-24 bg-blue-600 text-white border-2 border-blue-700 rounded shadow-xl flex flex-col items-center justify-center p-1.5 text-xs opacity-90 pointer-events-none">
                        {activeIsBatch ? (
                            <>
                                <CheckSquare className="w-4 h-4 mb-1" />
                                <div className="font-mono font-bold">{selectedBinCodes.size} valgte</div>
                            </>
                        ) : (
                            <>
                                <Move className="w-4 h-4 mb-1" />
                                <div className="font-mono font-bold">{activeBinCode}</div>
                            </>
                        )}
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};
